import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hasPermission, isManager, getCurrentUser } from '../../services/authService';
import { authGet } from '../../services/apiService';
import {
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Typography,
  Paper
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  BarChart as ReportIcon,
  Settings as SettingsIcon,
  VerifiedUser as VerifiedIcon,
  Science as TestEngineeringIcon,
  BugReport as BugIcon,
  SwapHoriz as MigrationsIcon,
  Speed as SprintMetricsIcon,
  FactCheck as ShaIcon,
  FindInPage as PlatformInvestigationIcon,
  AutoAwesome as AIToolkitIcon,
  Shield as GuardrailIcon,
  SupportAgent as CustomerIssueIcon,
  Policy as AuditConsoleIcon,
  Article as ConfidencePlanIcon,
} from '@mui/icons-material';

const Sidebar = () => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const isAdminUser = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';
  const isManagerUser = isManager();
  const canManageWorkItems = hasPermission('workitems:write');
  const canManageQA = hasPermission('qa:admin');
  const darkGreenColor = "#1D4434";

  useEffect(() => {
    authGet('/api/auth/me').then(data => {
      if (data?.user) {
        localStorage.setItem('user', JSON.stringify(data.user));
        setCurrentUser(data.user);
      }
    }).catch(() => {});
  }, []);

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const menuItemSx = {
    borderRadius: 2,
    mx: 1,
    my: 0.5,
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: 'rgba(26, 46, 34, 0.06)',
      transform: 'translateX(2px)',
    },
    '&.Mui-selected': {
      backgroundColor: 'rgba(215, 75, 0, 0.12)',
      '&:hover': { backgroundColor: 'rgba(215, 75, 0, 0.16)' },
      '& .MuiListItemText-primary': { color: 'secondary.main', fontWeight: 500 },
    },
  };

  const subMenuItemSx = { ...menuItemSx, pl: 4 };

  const sectionHeaderSx = {
    padding: '16px 16px 8px',
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.08em',
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: 250,
        minWidth: 250,
        maxWidth: 250,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        borderRadius: 0,
        flexShrink: 0,
        bgcolor: 'background.paper',
        borderRight: '1px solid rgba(0, 0, 0, 0.06)',
      }}
    >
      <List component="nav" sx={{ py: 1 }}>

        {/* ── Main ── */}
        <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
          Main
        </Typography>

        <ListItem button component={Link} to="/" selected={isActive('/')} sx={menuItemSx}>
          <ListItemIcon><DashboardIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
          <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: isActive('/') ? 500 : 400 }} />
        </ListItem>

        {/* ── Quality Assurance ── */}
        <Divider sx={{ margin: '8px 0' }} />
        <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
          Quality Assurance
        </Typography>

        <ListItem button component={Link} to="/quality-assurance" selected={isActive('/quality-assurance')} sx={menuItemSx}>
          <ListItemIcon><VerifiedIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
          <ListItemText primary="Quality Assurance" primaryTypographyProps={{ fontWeight: isActive('/quality-assurance') ? 500 : 400 }} />
        </ListItem>

        <ListItem button component={Link} to="/test-engineering" selected={isActive('/test-engineering')} sx={subMenuItemSx}>
          <ListItemIcon><TestEngineeringIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
          <ListItemText primary="Test Engineering" primaryTypographyProps={{ fontWeight: isActive('/test-engineering') ? 500 : 400 }} />
        </ListItem>

        <ListItem button component={Link} to="/release-notes" selected={isActive('/release-notes')} sx={subMenuItemSx}>
          <ListItemIcon><ReportIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
          <ListItemText primary="Release Notes" primaryTypographyProps={{ fontWeight: isActive('/release-notes') ? 500 : 400 }} />
        </ListItem>

        <ListItem
          button
          component={Link}
          to="/?section=internal-bugs"
          selected={location.pathname === '/' && new URLSearchParams(location.search).get('section') === 'internal-bugs'}
          sx={subMenuItemSx}
        >
          <ListItemIcon><BugIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
          <ListItemText
            primary="Internal Bugs"
            primaryTypographyProps={{
              fontWeight: (location.pathname === '/' && new URLSearchParams(location.search || '').get('section') === 'internal-bugs') ? 500 : 400,
            }}
          />
        </ListItem>

        {/* ── Scrum Metrics ── */}
        <Divider sx={{ margin: '8px 0' }} />
        <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
          Scrum Metrics
        </Typography>

        <ListItem button component={Link} to="/sprint-metrics" selected={isActive('/sprint-metrics')} sx={menuItemSx}>
          <ListItemIcon><SprintMetricsIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
          <ListItemText primary="Sprint Metrics" primaryTypographyProps={{ fontWeight: isActive('/sprint-metrics') ? 500 : 400 }} />
        </ListItem>

        {/* ── AI Toolkit ── */}
        <Divider sx={{ margin: '8px 0' }} />
        <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
          AI Toolkit
        </Typography>

        <ListItem button component={Link} to="/ai-toolkit" selected={isActive('/ai-toolkit')} sx={menuItemSx}>
          <ListItemIcon><AIToolkitIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
          <ListItemText primary="AI Toolkit" primaryTypographyProps={{ fontWeight: isActive('/ai-toolkit') ? 500 : 400 }} />
        </ListItem>

        {/* ── Guardrail (access-gated, shown in main nav for all roles) ── */}
        {currentUser?.guardrailAccess === true && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
              Platform &amp; Ops
            </Typography>
            <ListItem button component={Link} to="/guardrail" selected={isActive('/guardrail')} sx={menuItemSx}>
              <ListItemIcon><GuardrailIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
              <ListItemText primary="Guardrail" primaryTypographyProps={{ fontWeight: isActive('/guardrail') ? 500 : 400 }} />
            </ListItem>
          </>
        )}

        {/* ── Platform & Ops (end users only) ── */}
        {!isAdminUser && (
          <>
            {!currentUser?.guardrailAccess && (
              <>
                <Divider sx={{ margin: '8px 0' }} />
                <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
                  Platform &amp; Ops
                </Typography>
              </>
            )}

            <ListItem button component={Link} to="/migrations" selected={isActive('/migrations')} sx={menuItemSx}>
              <ListItemIcon><MigrationsIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
              <ListItemText primary="Migrations" primaryTypographyProps={{ fontWeight: isActive('/migrations') ? 500 : 400 }} />
            </ListItem>

            <ListItem button component={Link} to="/sha" selected={isActive('/sha')} title="SHA — Service Health Assessment" sx={menuItemSx}>
              <ListItemIcon><ShaIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
              <ListItemText
                primary="SHA"
                secondary="Service Health Assessment"
                primaryTypographyProps={{ fontWeight: isActive('/sha') ? 500 : 400 }}
                secondaryTypographyProps={{ variant: 'caption', sx: { opacity: 0.75, lineHeight: 1.2 } }}
              />
            </ListItem>

            <ListItem button component={Link} to="/service-operations" selected={isActive('/service-operations')} sx={menuItemSx}>
              <ListItemIcon><PlatformInvestigationIcon sx={{ color: darkGreenColor, fontSize: 'small' }} /></ListItemIcon>
              <ListItemText primary="Service Operations" primaryTypographyProps={{ fontWeight: isActive('/service-operations') ? 500 : 400 }} />
            </ListItem>

            <ListItem button component={Link} to="/incidents-alerts" selected={isActive('/incidents-alerts')} sx={menuItemSx}>
              <ListItemIcon><VerifiedIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
              <ListItemText primary="Major Incidents" primaryTypographyProps={{ fontWeight: isActive('/incidents-alerts') ? 500 : 400 }} />
            </ListItem>
          </>
        )}

        {currentUser?.operationalExcellenceAccess === true && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
              Operational Excellence
            </Typography>

            <ListItem button component={Link} to="/operational-excellence" selected={isActive('/operational-excellence')} sx={menuItemSx}>
              <ListItemIcon><CustomerIssueIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
              <ListItemText primary="HighQ Vital Signs" primaryTypographyProps={{ fontWeight: isActive('/operational-excellence') ? 500 : 400 }} />
            </ListItem>

            <ListItem button component={Link} to="/audit-console" selected={isActive('/audit-console')} sx={menuItemSx}>
              <ListItemIcon><AuditConsoleIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
              <ListItemText primary="HighQ Audit Console" primaryTypographyProps={{ fontWeight: isActive('/audit-console') ? 500 : 400 }} />
            </ListItem>

            <ListItem button component={Link} to="/confidence-plan" selected={isActive('/confidence-plan') || isActive('/incident-remediation')} sx={menuItemSx}>
              <ListItemIcon><ConfidencePlanIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
              <ListItemText primary="Confidence Plan" primaryTypographyProps={{ fontWeight: (isActive('/confidence-plan') || isActive('/incident-remediation')) ? 500 : 400 }} />
            </ListItem>
          </>
        )}

        {/* ── Admin ── */}
        {isAdminUser && (
          <>
            <Divider sx={{ margin: '8px 0' }} />
            <Typography variant="overline" color="text.secondary" sx={sectionHeaderSx}>
              Admin
            </Typography>

            <ListItem button component={Link} to="/admin" selected={isActive('/admin')} sx={menuItemSx}>
              <ListItemIcon><SettingsIcon sx={{ color: darkGreenColor }} /></ListItemIcon>
              <ListItemText primary="Admin Panel" primaryTypographyProps={{ fontWeight: isActive('/admin') ? 500 : 400 }} />
            </ListItem>
          </>
        )}

      </List>
    </Paper>
  );
};

export default Sidebar;
